import {
  DataTable,
  DataTableSkeleton,
  MenuButton,
  MenuItem,
  MultiSelect,
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import { Send } from '@carbon/icons-react';
import {
  asProblem,
  ChoiceDropdown,
  EmptyState,
  FilterButton,
  FilterLayout,
  FormModal,
  hasPermission,
  problemMessage,
  StatusTag,
  useAdmin,
  useFilters,
  useFormat,
  type RecordValues,
} from '@pos/admin-ui';
import { fold, useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ErrorNotice, TransientNotice } from '../common/Notices';
import { loadStoreGroups, loadStoreGroupStoreIds } from '../common/storeGroups';
import './pos.scss';

const STATUSES = ['outdated', 'pending', 'never', 'current'] as const;
// The shared StatusTag colours by status code; these are the sync statuses mapped to the codes of the same meaning.
const TAG_STATUS: Record<string, string> = { current: 'active', pending: 'pending', never: 'new', outdated: 'failed' };

async function loadTargets(profileId: string) {
  const { data, error } = await hoApi.GET('/api/v1/sync/pos-profiles/{profileId}/stores', {
    params: { path: { profileId } },
  });
  if (!data) throw error;
  return data;
}

/**
 * Syncs a POS profile with its layouts to chosen stores (ADR 0016). Edits at HO reach no store until they are synced
 * here; each store shows whether it has the current version and how many of its terminals use the profile.
 */
export function PosProfileSyncTab({ profile }: { profile: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canSync = hasPermission(session, 'ho.config.pos_profiles.manage');
  // Choosing a store group fills the confirmation with its stores (ADR 0115); reading groups needs the organisation permission.
  const canPickGroup = canSync && hasPermission(session, 'ho.org.store_groups.read');
  const profileId = String(profile.id);
  const queryClient = useQueryClient();
  const key = ['ho', 'pos-profile-targets', profileId];
  const targets = useQuery({ queryKey: key, queryFn: () => loadTargets(profileId) });
  const filters = useFilters({ status: '', used: '' });
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState<string[]>();
  const [done, setDone] = useState<number>();
  const [emptyGroup, setEmptyGroup] = useState<string>();
  const brandId = targets.data?.brandId ?? null;
  const groups = useQuery({
    queryKey: ['ho', 'store-groups', brandId],
    queryFn: () => loadStoreGroups(brandId),
    enabled: canPickGroup && targets.isSuccess,
  });

  const publish = useMutation({
    mutationFn: async (storeIds: string[]) => {
      const { data, error } = await hoApi.POST('/api/v1/sync/pos-profiles/{profileId}/publish', {
        params: { path: { profileId } },
        body: { storeIds },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (result) => {
      setConfirming(undefined);
      setDone(Number(result.storeCount));
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const pickGroup = useMutation({
    mutationFn: async (group: { id: string; name: string }) => ({ group, storeIds: await loadStoreGroupStoreIds(group.id) }),
    onSuccess: ({ group, storeIds }) => {
      const members = new Set(storeIds);
      const chosen = (targets.data?.stores ?? []).filter((s) => members.has(s.storeId)).map((s) => s.storeId);
      if (chosen.length === 0) setEmptyGroup(group.name);
      else setConfirming(chosen);
    },
  });
  const profileGroups = groups.data ?? [];

  const statuses = filters.applied.status ? filters.applied.status.split(',') : [];
  const stores = (targets.data?.stores ?? []).filter(
    (s) =>
      (statuses.length === 0 || statuses.includes(s.status)) &&
      (filters.applied.used === '' || (filters.applied.used === 'yes') === Number(s.terminalCount) > 0) &&
      (!search || fold(`${s.storeCode} ${s.storeName}`).includes(fold(search))),
  );
  const byId = new Map(stores.map((s) => [s.storeId, s]));
  const statusItems = STATUSES.map((id) => ({ id, label: t(`posSync.status.${id}` as I18nKey) }));
  const draftStatuses = filters.draft.status ? filters.draft.status.split(',') : [];
  const headers = [
    { key: 'store', header: t('posSync.columns.store') },
    { key: 'terminals', header: t('posSync.columns.terminals') },
    { key: 'status', header: t('posSync.columns.status') },
    { key: 'synced', header: t('posSync.columns.synced') },
  ];

  if (targets.isLoading)
    return (
      <DataTableSkeleton
        columnCount={headers.length}
        rowCount={5}
        headers={headers}
        showToolbar
        aria-label={t('common.loading')}
      />
    );
  if (!targets.data)
    return <ErrorNotice title={problemMessage(t, asProblem(targets.error))} onRetry={() => void targets.refetch()} />;

  return (
    <div className="pos-profile-sync">
      <p className="pos-profile-sync__hint">
        {t('posSync.hint')} {t('posSync.changedAt', { time: format.dateTime(targets.data.changedAt) })}
      </p>
      {done !== undefined && (
        <TransientNotice kind="success" title={t('posSync.done', { count: done })} onDone={() => setDone(undefined)} />
      )}
      {emptyGroup !== undefined && (
        <TransientNotice kind="warning" title={t('posSync.groupEmpty', { group: emptyGroup })} onDone={() => setEmptyGroup(undefined)} />
      )}
      {pickGroup.error && <ErrorNotice title={problemMessage(t, asProblem(pickGroup.error))} onRetry={() => pickGroup.reset()} />}
      <FilterLayout
        filters={filters}
        fields={
          <>
            <MultiSelect<{ id: string; label: string }>
              id="pos-sync-status"
              titleText={t('posSync.columns.status')}
              label={t('posSync.columns.status')}
              items={statusItems}
              itemToString={(o) => o?.label ?? ''}
              selectedItems={statusItems.filter((o) => draftStatuses.includes(o.id))}
              onChange={({ selectedItems }) => filters.set('status', (selectedItems ?? []).map((o) => o.id).join(','))}
              selectionFeedback="fixed"
            />
            <ChoiceDropdown
              id="pos-sync-used"
              label={t('posSync.usedBy')}
              choices={[
                { id: 'yes', label: t('posSync.usedYes') },
                { id: 'no', label: t('posSync.usedNo') },
              ]}
              value={filters.draft.used}
              onChange={(v) => filters.set('used', v)}
            />
          </>
        }
      >
        <DataTable rows={stores.map((s) => ({ id: s.storeId }))} headers={headers}>
          {({
            rows,
            headers: tableHeaders,
            getTableProps,
            getHeaderProps,
            getRowProps,
            getSelectionProps,
            getToolbarProps,
            getBatchActionProps,
            selectedRows,
          }) => {
            const batch = getBatchActionProps();
            return (
              <TableContainer>
                <TableToolbar {...getToolbarProps()}>
                  {canSync && (
                    <TableBatchActions
                      {...batch}
                      translateWithId={(id: string, state?: { totalSelected?: number }) =>
                        id === 'carbon.table.batch.cancel'
                          ? t('common.cancel')
                          : t('posSync.selected', { count: state?.totalSelected ?? selectedRows.length })
                      }
                    >
                      <TableBatchAction renderIcon={Send} onClick={() => setConfirming(selectedRows.map((r) => r.id))}>
                        {t('posSync.sync', { count: selectedRows.length })}
                      </TableBatchAction>
                    </TableBatchActions>
                  )}
                  <TableToolbarContent className="pos-table-toolbar__content" aria-hidden={batch.shouldShowBatchActions}>
                    <TableToolbarSearch
                      className="pos-table-toolbar__search"
                      persistent
                      labelText={t('admin.search')}
                      placeholder={t('admin.search')}
                      closeButtonLabelText={t('carbon.clearSearch')}
                      tabIndex={batch.shouldShowBatchActions ? -1 : 0}
                      onChange={(_event, value) => setSearch(value ?? '')}
                    />
                    <FilterButton filters={filters} />
                    {canPickGroup && profileGroups.length > 0 && (
                      <MenuButton
                        label={t('posSync.syncGroup')}
                        kind="ghost"
                        size="lg"
                        menuAlignment="bottom-end"
                        tabIndex={batch.shouldShowBatchActions ? -1 : 0}
                        disabled={pickGroup.isPending}
                      >
                        {profileGroups.map((g) => (
                          <MenuItem key={g.id} label={`${g.code} · ${g.name}`} onClick={() => pickGroup.mutate({ id: g.id, name: g.name })} />
                        ))}
                      </MenuButton>
                    )}
                  </TableToolbarContent>
                </TableToolbar>
                <Table {...getTableProps()} aria-label={t('posSync.title')}>
                  <TableHead>
                    <TableRow>
                      {canSync && <TableSelectAll {...getSelectionProps()} />}
                      {tableHeaders.map((header) => {
                        const { key: headerKey, ...props } = getHeaderProps({ header });
                        return (
                          <TableHeader key={headerKey} {...props}>
                            {header.header}
                          </TableHeader>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const store = byId.get(row.id);
                      if (!store) return null;
                      const { key: rowKey, ...rowProps } = getRowProps({ row });
                      return (
                        <TableRow key={rowKey} {...rowProps}>
                          {canSync && <TableSelectRow {...getSelectionProps({ row })} />}
                          <TableCell>
                            {store.storeCode} · {store.storeName}
                          </TableCell>
                          <TableCell className="pos-profile-sync__number">
                            {format.number(store.terminalCount)}
                          </TableCell>
                          <TableCell>
                            <StatusTag status={TAG_STATUS[store.status] ?? store.status}>
                              {t(`posSync.status.${store.status}` as I18nKey, { defaultValue: store.status })}
                            </StatusTag>
                            {store.storeError && <p className="pos-profile-sync__error">{store.storeError}</p>}
                          </TableCell>
                          <TableCell>{store.lastPublishedAt ? format.dateTime(store.lastPublishedAt) : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {rows.length === 0 &&
                  (targets.data.stores.length === 0 ? (
                    <EmptyState heading={t('posSync.emptyTitle')} description={t('posSync.empty')} />
                  ) : (
                    <EmptyState heading={t('admin.noResultsTitle')} description={t('admin.noResultsHint')} />
                  ))}
              </TableContainer>
            );
          }}
        </DataTable>
      </FilterLayout>

      {confirming && (
        <FormModal
          title={t('posSync.confirmTitle', { count: confirming.length, profile: String(profile.name ?? '') })}
          label={t('posSync.title')}
          primaryText={t('posSync.confirm')}
          pending={publish.isPending}
          error={publish.error ? problemMessage(t, asProblem(publish.error)) : null}
          onSubmit={() => publish.mutate(confirming)}
          onClose={() => {
            publish.reset();
            setConfirming(undefined);
          }}
        >
          <p>{t('posSync.confirmBody')}</p>
          <ul className="pos-profile-sync__list">
            {confirming.map((id) => {
              const store = targets.data.stores.find((s) => s.storeId === id);
              return <li key={id}>{store ? `${store.storeCode} · ${store.storeName}` : id}</li>;
            })}
          </ul>
        </FormModal>
      )}
    </div>
  );
}
